import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { candidatesApi, savedSearchesApi, workbenchApi, applicationsApi, jobsApi, aiApi, usersApi, getErrorMessage, isAuthError } from '../services/api';
import UserPicker from '../components/UserPicker';
import { useAuth } from '../context/AuthContext';
import { canSeeRevenue } from '../lib/perms';
import { ColumnsMenu, useHiddenColumns } from '../components/common/ColumnsMenu';
import { DensityToggle, useDensity } from '../components/common/DensityToggle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';

// Import refactored components
import { CandidateTable, CandidateViewDialog, WorkbenchDialog, BulkShortlistDialog, FindDuplicatesDialog } from '../components/candidates';
import CandidateFormContent from '../components/CandidateFormContent';

import {
  Plus,
  Search,
  Upload,
  Sparkles,
  User,
  Mail,
  Brain,
  Target,
  Phone,
  MapPin,
  Building2,
  Loader2,
  FileText,
  X,
  Check,
  Files,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  ChevronDown,
  Filter,
  Zap,
  Bookmark,
  BookmarkPlus,
  Star,
  Copy,
  AlertTriangle,
  Users,
  FileSpreadsheet,
  Download,
  ClipboardList,
  Trash2,
  Briefcase,
  Cake,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const CANDIDATES_COLUMNS = [
  { key: 'candidate', label: 'Candidate', required: true },
  { key: 'contact', label: 'Contact' },
  { key: 'experience', label: 'Experience' },
  { key: 'skills', label: 'Skills' },
  { key: 'source', label: 'Source' },
  { key: 'uploaded_by', label: 'Uploaded by' },
  { key: 'added', label: 'Added' },
  { key: 'last_edited', label: 'Last edited' },
  { key: 'actions', label: 'Actions', required: true },
];

const Candidates = () => {
  const { user } = useAuth();
  const showRevenue = canSeeRevenue(user);
  const isAdminUser = user?.role === 'admin' || user?.role === 'delivery_manager';
  const [candidates, setCandidates] = useState([]);
  // Persisted column visibility for the candidates table.
  const [hiddenCols, setHiddenCols] = useHiddenColumns('candidates-table-cols');
  const [density, setDensity, densityClass] = useDensity('candidates-table-density');
  const [brokenResumeCount, setBrokenResumeCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillsFilter, setSkillsFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  // Sorting + uploaded-by filter (added Feb 2026)
  const [sortBy, setSortBy] = useState('created_at'); // created_at | name | experience_years
  const [sortOrder, setSortOrder] = useState('desc'); // asc | desc
  const [uploadedByFilter, setUploadedByFilter] = useState(''); // '' (anyone), 'me', or user id
  const [teammates, setTeammates] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [uploadedResumeText, setUploadedResumeText] = useState('');
  const [uploadedResumeGridfsId, setUploadedResumeGridfsId] = useState('');
  const [uploadedResumeFilePath, setUploadedResumeFilePath] = useState('');
  
  // Advanced search state
  const [advancedSearchMode, setAdvancedSearchMode] = useState(false);
  const [advancedQuery, setAdvancedQuery] = useState('');
  const [minExp, setMinExp] = useState('');
  const [maxExp, setMaxExp] = useState('');
  const [minCurrentCtc, setMinCurrentCtc] = useState('');
  const [maxCurrentCtc, setMaxCurrentCtc] = useState('');
  const [minExpectedCtc, setMinExpectedCtc] = useState('');
  const [maxExpectedCtc, setMaxExpectedCtc] = useState('');
  const [maxNoticePeriod, setMaxNoticePeriod] = useState('');
  const [educationFilter, setEducationFilter] = useState(''); // comma-separated tokens
  const [locationFilterAdv, setLocationFilterAdv] = useState(''); // advanced-search location chips, comma-separated
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showSearchHelp, setShowSearchHelp] = useState(false);
  
  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  
  // Excel upload state
  const [excelFile, setExcelFile] = useState(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [excelProgress, setExcelProgress] = useState(0);
  const [excelResults, setExcelResults] = useState(null);
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState(false);
  
  // Bulk re-upload state (for matching existing candidates)
  const [isBulkReuploadOpen, setIsBulkReuploadOpen] = useState(false);
  const [bulkReuploadFiles, setBulkReuploadFiles] = useState([]);
  const [bulkReuploadUploading, setBulkReuploadUploading] = useState(false);
  const [bulkReuploadProgress, setBulkReuploadProgress] = useState(0);
  const [bulkReuploadResults, setBulkReuploadResults] = useState(null);
  const [bulkReuploadDragging, setBulkReuploadDragging] = useState(false);
  
  // Saved searches state
  const [savedSearches, setSavedSearches] = useState([]);
  const [isSaveSearchOpen, setIsSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [savingSearch, setSavingSearch] = useState(false);
  const [loadingSavedSearches, setLoadingSavedSearches] = useState(false);
  
  // Duplicate detection state
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [showDuplicatesWarning, setShowDuplicatesWarning] = useState(false);
  
  // Workbench state
  const [isWorkbenchOpen, setIsWorkbenchOpen] = useState(false);
  const [workbenchCandidate, setWorkbenchCandidate] = useState(null);
  const [workbenchRemark, setWorkbenchRemark] = useState('');
  const [workbenchStatus, setWorkbenchStatus] = useState('');
  const [workbenchPrivate, setWorkbenchPrivate] = useState(true);
  const [workbenchLoading, setWorkbenchLoading] = useState(false);
  // iter_38: Optional "Also add to a job's pipeline" picker inside the workbench dialog
  const [workbenchPipelineJobId, setWorkbenchPipelineJobId] = useState('');
  const [workbenchJobs, setWorkbenchJobs] = useState([]);

  // iter_39: Bulk-shortlist (multi-select candidates → "Add to Pipeline" → pick one job)
  const [selectedCandidateIds, setSelectedCandidateIds] = useState(new Set());
  const [isBulkShortlistOpen, setIsBulkShortlistOpen] = useState(false);
  const [bulkShortlistJobId, setBulkShortlistJobId] = useState('');
  const [bulkShortlistRemarks, setBulkShortlistRemarks] = useState('');
  const [bulkShortlistLoading, setBulkShortlistLoading] = useState(false);

  // iter_42: Find duplicates dialog
  const [isFindDuplicatesOpen, setIsFindDuplicatesOpen] = useState(false);

  // iter_49: One-shot DOB backfill (admin only)
  const [backfillLoading, setBackfillLoading] = useState(false);

  const handleBackfillDob = async () => {
    setBackfillLoading(true);
    try {
      // Step 1: dry_run to preview the impact (cheap — same query, no writes)
      const preview = await candidatesApi.backfillDob(1000, true);
      const wouldUpdate = preview?.data?.extracted ?? 0;
      const scanned = preview?.data?.scanned ?? 0;
      if (wouldUpdate === 0) {
        toast.info(
          scanned === 0
            ? 'No candidates need backfill — everyone already has a DOB or no resume text.'
            : `Scanned ${scanned} candidates with resume text — none had a parseable DOB inside.`
        );
        return;
      }
      // Step 2: real run
      const real = await candidatesApi.backfillDob(1000, false);
      const r = real?.data || {};
      const remaining = r.remaining ?? 0;
      toast.success(
        `Updated ${r.updated} candidate${r.updated === 1 ? '' : 's'} with DOB extracted from resume text` +
          (remaining > 0
            ? ` · ${remaining} still need backfill (run again to process the next batch)`
            : ' · all done')
      );
      // Refresh the list so any visible candidate that just got a DOB shows it
      fetchCandidates();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Backfill failed';
      toast.error(typeof detail === 'string' ? detail : 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };
  
  // AI Features state
  const [isAISummaryOpen, setIsAISummaryOpen] = useState(false);
  const [aiSummaryData, setAiSummaryData] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [isRecommendationsOpen, setIsRecommendationsOpen] = useState(false);
  const [recommendationsData, setRecommendationsData] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  
  // Resume upload state
  const [isResumeUploadOpen, setIsResumeUploadOpen] = useState(false);
  const [resumeUploadCandidate, setResumeUploadCandidate] = useState(null);
  const [resumeUploadFile, setResumeUploadFile] = useState(null);
  const [resumeUploadLoading, setResumeUploadLoading] = useState(false);
  
  // Predefined candidate statuses for workbench
  const candidateStatuses = [
    'Not Responding',
    'Switch Off',
    'Call Back',
    'Number Busy',
    'Not Selected',
    'Not Interested',
    'Recently got a job',
    'Not Reachable',
    'Invalid No',
    'Looking for WFH',
    'Call Disconnected',
    'Selected'
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    current_location: '',
    experience_years: 0,
    current_company: '',
    current_designation: '',
    skills: '',
    education: '',
    resume_text: '',
    source: 'manual',
    notes: '',
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    date_of_birth: '',
  });

  // iter_45: Pagination state for the candidates list. Mirrors the /clients
  // pattern so the UI scales cleanly past the first page.
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // iter_45: debounce the search term sent to the backend so 4 quick
  // keystrokes -> 1 API call.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 200);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // iter_45: re-fetch when filters/search change OR when the page changes.
  // We merge the page-1 reset INTO this effect (instead of a separate effect)
  // so we never fire a fetch with the stale page on the same render cycle.
  // The pageRef holds the previous filter signature; when it changes we
  // both reset to page 1 AND fetch, in a single tick.
  const filterSigRef = React.useRef('');
  useEffect(() => {
    const sig = `${debouncedSearch}|${skillsFilter}|${locationFilter}|${sortBy}|${sortOrder}|${uploadedByFilter}`;
    if (filterSigRef.current !== sig) {
      filterSigRef.current = sig;
      // Filter signature changed → snap back to page 1. If we were already on
      // page 1, fetch directly; otherwise setPagination triggers a re-render
      // with page=1 and the next pass of THIS same effect (with sig unchanged)
      // will run the fetch.
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        return;
      }
    }
    fetchCandidates();
    fetchSavedSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillsFilter, locationFilter, debouncedSearch, sortBy, sortOrder, uploadedByFilter, pagination.page]);

  // Fetch teammates once (drives the "Uploaded by" dropdown). Silent on
  // failure — the filter still works with the simpler "My uploads / Anyone"
  // toggle if the user list is unavailable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await usersApi.getAll();
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setTeammates(list.filter((u) => u && u.id && u.name).map((u) => ({ id: u.id, name: u.name })));
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Admin-only: fetch broken-resume count once on mount for header badge
  useEffect(() => {
    if (!isAdminUser) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await candidatesApi.cleanupInvalidResumes(true); // dry-run
        if (!cancelled) {
          setBrokenResumeCount(res.data?.invalid_references ?? 0);
        }
      } catch (e) {
        // Silent fail — badge simply won't render
        if (!cancelled) setBrokenResumeCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdminUser]);

  const fetchSavedSearches = async () => {
    setLoadingSavedSearches(true);
    try {
      const response = await savedSearchesApi.getAll();
      setSavedSearches(response.data);
    } catch (error) {
      console.error('Failed to load saved searches:', error);
    } finally {
      setLoadingSavedSearches(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (skillsFilter) params.skills = skillsFilter;
      if (locationFilter) params.location = locationFilter;
      if (uploadedByFilter) params.created_by = uploadedByFilter;

      const response = await candidatesApi.getAll(params);
      const data = response.data;
      setCandidates(data.items || (Array.isArray(data) ? data : []));
      // Sync pagination metadata from the server response so the footer
      // can do its math (and only render when total_pages > 1).
      setPagination((prev) => ({
        ...prev,
        total: data.total ?? (Array.isArray(data) ? data.length : 0),
        totalPages: data.total_pages ?? 1,
        hasNext: !!data.has_next,
        hasPrev: !!data.has_prev,
      }));
      setSearchResults(null);
    } catch (error) {
      // Don't show error toast for auth errors - interceptor handles redirect
      if (!isAuthError(error)) {
        console.error('Failed to load candidates:', error);
        toast.error(getErrorMessage(error, 'Failed to load candidates. Please try refreshing the page.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (advancedSearchMode && advancedQuery) {
      handleAdvancedSearch();
    } else {
      setLoading(true);
      fetchCandidates();
    }
  };

  const handleAdvancedSearch = async () => {
    setLoading(true);
    try {
      const response = await candidatesApi.advancedSearch({
        query: advancedQuery,
        minExp: minExp ? parseFloat(minExp) : null,
        maxExp: maxExp ? parseFloat(maxExp) : null,
        minCurrentCtc: minCurrentCtc ? parseFloat(minCurrentCtc) : null,
        maxCurrentCtc: maxCurrentCtc ? parseFloat(maxCurrentCtc) : null,
        minExpectedCtc: minExpectedCtc ? parseFloat(minExpectedCtc) : null,
        maxExpectedCtc: maxExpectedCtc ? parseFloat(maxExpectedCtc) : null,
        maxNoticePeriod: maxNoticePeriod ? parseInt(maxNoticePeriod) : null,
        education: educationFilter.trim() || null,
        location: locationFilterAdv.trim() || null,
        minAge: minAge !== '' ? parseInt(minAge) : null,
        maxAge: maxAge !== '' ? parseInt(maxAge) : null,
      });
      setSearchResults(response.data);
      setCandidates(response.data.candidates || []);
      toast.success(`Found ${response.data.total} candidates`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Search failed'));
    } finally {
      setLoading(false);
    }
  };

  const clearAdvancedSearch = () => {
    setAdvancedQuery('');
    setMinExp('');
    setMaxExp('');
    setMinCurrentCtc('');
    setMaxCurrentCtc('');
    setMinExpectedCtc('');
    setMaxExpectedCtc('');
    setMaxNoticePeriod('');
    setEducationFilter('');
    setLocationFilterAdv('');
    setMinAge('');
    setMaxAge('');
    setSearchResults(null);
    fetchCandidates();
  };

  // Duplicate detection
  const checkForDuplicates = async (name, email, phone) => {
    if (!name && !email && !phone) return;
    
    setCheckingDuplicates(true);
    try {
      const response = await candidatesApi.checkDuplicates({ name, email, phone });
      if (response.data.has_duplicates) {
        setDuplicates(response.data.duplicates);
        setShowDuplicatesWarning(true);
      } else {
        setDuplicates([]);
        setShowDuplicatesWarning(false);
      }
    } catch (error) {
      console.error('Duplicate check failed:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const debouncedDuplicateCheck = useCallback(
    debounce((name, email, phone) => checkForDuplicates(name, email, phone), 500),
    []
  );

  // Saved searches
  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) return;
    // Require at least ONE filter or a boolean query — refuse empty saves so the list stays useful
    const hasFilter = advancedQuery.trim()
      || minExp || maxExp || minCurrentCtc || maxCurrentCtc
      || minExpectedCtc || maxExpectedCtc || maxNoticePeriod
      || educationFilter.trim() || locationFilterAdv.trim()
      || minAge !== '' || maxAge !== '';
    if (!hasFilter) {
      toast.error('Add at least one filter or boolean query before saving.');
      return;
    }
    
    setSavingSearch(true);
    try {
      await savedSearchesApi.create({
        name: saveSearchName,
        query: advancedQuery,
        min_exp: minExp ? parseFloat(minExp) : null,
        max_exp: maxExp ? parseFloat(maxExp) : null,
        // Persist new advanced filters via the flexible `filters` blob so they're restored on re-run
        filters: {
          minCurrentCtc: minCurrentCtc || null,
          maxCurrentCtc: maxCurrentCtc || null,
          minExpectedCtc: minExpectedCtc || null,
          maxExpectedCtc: maxExpectedCtc || null,
          maxNoticePeriod: maxNoticePeriod || null,
          education: educationFilter || null,
          location: locationFilterAdv || null,
          minAge: minAge || null,
          maxAge: maxAge || null,
        },
      });
      toast.success('Search saved successfully!');
      setIsSaveSearchOpen(false);
      setSaveSearchName('');
      fetchSavedSearches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save search'));
    } finally {
      setSavingSearch(false);
    }
  };

  const handleRunSavedSearch = async (search) => {
    setLoading(true);
    try {
      const f = search.filters || {};
      // Restore all filters into the UI state
      setAdvancedQuery(search.query || '');
      setMinExp(search.min_exp?.toString() || '');
      setMaxExp(search.max_exp?.toString() || '');
      setMinCurrentCtc(f.minCurrentCtc?.toString() || '');
      setMaxCurrentCtc(f.maxCurrentCtc?.toString() || '');
      setMinExpectedCtc(f.minExpectedCtc?.toString() || '');
      setMaxExpectedCtc(f.maxExpectedCtc?.toString() || '');
      setMaxNoticePeriod(f.maxNoticePeriod?.toString() || '');
      setEducationFilter(f.education?.toString() || '');
      setLocationFilterAdv(f.location?.toString() || '');
      setMinAge(f.minAge?.toString() || '');
      setMaxAge(f.maxAge?.toString() || '');
      setAdvancedSearchMode(true);

      // Re-run the search with the restored filters via the same advancedSearch endpoint
      const response = await candidatesApi.advancedSearch({
        query: search.query || '',
        minExp: search.min_exp,
        maxExp: search.max_exp,
        minCurrentCtc: f.minCurrentCtc ? parseFloat(f.minCurrentCtc) : null,
        maxCurrentCtc: f.maxCurrentCtc ? parseFloat(f.maxCurrentCtc) : null,
        minExpectedCtc: f.minExpectedCtc ? parseFloat(f.minExpectedCtc) : null,
        maxExpectedCtc: f.maxExpectedCtc ? parseFloat(f.maxExpectedCtc) : null,
        maxNoticePeriod: f.maxNoticePeriod ? parseInt(f.maxNoticePeriod) : null,
        education: f.education || null,
        location: f.location || null,
        minAge: f.minAge != null && f.minAge !== '' ? parseInt(f.minAge) : null,
        maxAge: f.maxAge != null && f.maxAge !== '' ? parseInt(f.maxAge) : null,
      });
      setSearchResults(response.data);
      setCandidates(response.data.candidates || []);
      toast.success(`Found ${response.data.total} candidates`);
      // Best-effort: bump use_count via the legacy run endpoint (ignore if it 404s)
      savedSearchesApi.run(search.id).catch(() => {});
      fetchSavedSearches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to run search'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSavedSearch = async (searchId) => {
    if (!window.confirm('Delete this saved search?')) return;
    
    try {
      await savedSearchesApi.delete(searchId);
      toast.success('Search deleted');
      fetchSavedSearches();
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  const handleFindSimilar = async (candidate) => {
    try {
      const response = await candidatesApi.findSimilar(candidate.id);
      if (response.data.has_duplicates) {
        toast.info(`Found ${response.data.count} similar candidate(s)`);
        // Show similar candidates in a modal or filter the list
        setDuplicates(response.data.duplicates);
        setSelectedCandidate(candidate);
        setShowSimilarModal(true);
      } else {
        toast.info('No similar candidates found');
      }
    } catch (error) {
      toast.error('Failed to find similar candidates');
    }
  };

  const [showSimilarModal, setShowSimilarModal] = useState(false);

  // AI Feature Handlers
  const handleAISummary = async (candidate) => {
    setSelectedCandidate(candidate);
    setIsAISummaryOpen(true);
    setAiSummaryLoading(true);
    setAiSummaryData(null);
    
    try {
      const response = await aiApi.getCandidateSummary(candidate.id);
      setAiSummaryData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate AI summary'));
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const handleJobRecommendations = async (candidate) => {
    setSelectedCandidate(candidate);
    setIsRecommendationsOpen(true);
    setRecommendationsLoading(true);
    setRecommendationsData(null);
    
    try {
      const response = await aiApi.getCandidateRecommendations(candidate.id, 5);
      setRecommendationsData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to get job recommendations'));
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      current_location: '',
      experience_years: 0,
      current_company: '',
      current_designation: '',
      skills: '',
      education: '',
      resume_text: '',
      source: 'manual',
      notes: '',
      current_ctc: '',
      expected_ctc: '',
      notice_period: '',
      date_of_birth: '',
    });
  };

  const handleCreate = async () => {
    setFormLoading(true);
    try {
      const data = {
        ...formData,
        skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
        experience_years: parseFloat(formData.experience_years) || 0,
        current_ctc: formData.current_ctc ? parseFloat(formData.current_ctc) : null,
        expected_ctc: formData.expected_ctc ? parseFloat(formData.expected_ctc) : null,
        notice_period: formData.notice_period ? parseInt(formData.notice_period) : null,
        date_of_birth: formData.date_of_birth || null,
      };

      await candidatesApi.create(data);
      toast.success('Candidate created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchCandidates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create candidate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    setFormLoading(true);
    try {
      const data = {
        ...formData,
        skills: typeof formData.skills === 'string'
          ? formData.skills.split(',').map((s) => s.trim()).filter(Boolean)
          : formData.skills,
        experience_years: parseFloat(formData.experience_years) || 0,
        current_ctc: formData.current_ctc ? parseFloat(formData.current_ctc) : null,
        expected_ctc: formData.expected_ctc ? parseFloat(formData.expected_ctc) : null,
        notice_period: formData.notice_period ? parseInt(formData.notice_period) : null,
        date_of_birth: formData.date_of_birth || null,
      };

      await candidatesApi.update(selectedCandidate.id, data);
      toast.success('Candidate updated successfully');
      setIsEditOpen(false);
      fetchCandidates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update candidate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (candidate) => {
    if (!window.confirm(`Are you sure you want to delete "${candidate.name || 'this candidate'}"?`)) return;

    try {
      await candidatesApi.delete(candidate.id);
      toast.success('Candidate deleted successfully');
      fetchCandidates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete candidate'));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadLoading(true);
    try {
      const response = await candidatesApi.uploadResume(file);
      setUploadedResumeText(response.data.resume_text);
      setUploadedResumeGridfsId(response.data.gridfs_id || '');
      setUploadedResumeFilePath(response.data.file_path || '');
      toast.success('Resume uploaded successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload resume'));
    } finally {
      setUploadLoading(false);
    }
  };

  const handleParseResume = async () => {
    if (!uploadedResumeText) return;

    setParseLoading(true);
    try {
      const response = await candidatesApi.parseResume(uploadedResumeText);
      const parsed = response.data;

      setFormData({
        name: parsed.name || '',
        email: parsed.email || '',
        phone: parsed.phone || '',
        current_location: parsed.current_location || '',
        experience_years: parsed.experience_years || 0,
        current_company: parsed.current_company || '',
        current_designation: parsed.current_designation || '',
        skills: Array.isArray(parsed.skills) ? parsed.skills.join(', ') : '',
        education: parsed.education || '',
        resume_text: uploadedResumeText,
        resume_url: uploadedResumeFilePath,
        gridfs_resume_id: uploadedResumeGridfsId,
        source: 'resume_upload',
        notes: '',
      });

      setIsUploadOpen(false);
      setIsCreateOpen(true);
      toast.success('Resume parsed successfully!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to parse resume'));
    } finally {
      setParseLoading(false);
    }
  };

  // Bulk upload handlers
  const MAX_BULK_FILES = 20;
  
  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => 
      f.name.endsWith('.pdf') || f.name.endsWith('.docx') || f.name.endsWith('.doc')
    );
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were skipped. Only PDF and DOCX are allowed.');
    }
    
    const newTotal = bulkFiles.length + validFiles.length;
    if (newTotal > MAX_BULK_FILES) {
      toast.error(`Maximum ${MAX_BULK_FILES} files allowed. You can add ${MAX_BULK_FILES - bulkFiles.length} more files.`);
      const allowedFiles = validFiles.slice(0, MAX_BULK_FILES - bulkFiles.length);
      setBulkFiles(prev => [...prev, ...allowedFiles]);
    } else {
      setBulkFiles(prev => [...prev, ...validFiles]);
    }
    setBulkResults(null);
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearBulkUpload = () => {
    setBulkFiles([]);
    setBulkResults(null);
    setBulkProgress(0);
    setIsDragging(false);
    setBulkOverwrite(false);
  };

  // Upload files in small chunks instead of one giant request. Production
  // hosts (Cloudflare / ingress) typically kill any request that runs past
  // ~100-120s, and the bulk endpoint takes ~10s/file (PDF parse + AI). 19
  // files in one shot = ~190s = guaranteed Cloudflare 524.
  // Chunking to BULK_UPLOAD_CHUNK at a time keeps each request well within
  // the proxy budget AND keeps the prod pod's peak heap low enough to
  // avoid OOM-induced Cloudflare 520s. 2 is the conservative sweet spot:
  // ~20s/chunk worst case, ~200MB peak heap per worker.
  const BULK_UPLOAD_CHUNK = 2;

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;

    setBulkUploading(true);
    setBulkProgress(0);
    setBulkResults(null);

    const aggregate = {
      total: bulkFiles.length,
      successful: 0,
      failed: 0,
      updated: 0,
      candidates: [],
      errors: [],
    };

    try {
      for (let i = 0; i < bulkFiles.length; i += BULK_UPLOAD_CHUNK) {
        const slice = bulkFiles.slice(i, i + BULK_UPLOAD_CHUNK);
        const baseDone = i; // files completed BEFORE this chunk started
        try {
          const response = await candidatesApi.bulkUpload(
            slice,
            (progressEvent) => {
              // Per-chunk upload bytes → global %. Network upload is the
              // first ~5% of total work; backend parse is the rest. We
              // weight uploads at 30% and processing at 70% per chunk.
              const chunkUploadPct = progressEvent.total
                ? (progressEvent.loaded / progressEvent.total) * 100
                : 0;
              const filesShare = (baseDone + (slice.length * chunkUploadPct * 0.3) / 100) / bulkFiles.length;
              setBulkProgress(Math.round(filesShare * 100));
            },
            bulkOverwrite,
          );
          const d = response.data || {};
          aggregate.successful += d.successful || 0;
          aggregate.failed += d.failed || 0;
          aggregate.updated += d.updated || 0;
          aggregate.candidates = aggregate.candidates.concat(d.candidates || []);
          aggregate.errors = aggregate.errors.concat(d.errors || []);
        } catch (chunkErr) {
          // One bad batch shouldn't kill the rest. Record each file in the
          // chunk as failed and keep going so the user sees a partial result.
          aggregate.failed += slice.length;
          slice.forEach((f) => {
            aggregate.errors.push({
              filename: f.name || 'unknown',
              error: chunkErr?.response?.data?.detail || chunkErr?.message || 'Chunk upload failed',
            });
          });
        }
        // After each chunk, refresh the global %.
        const completed = Math.min(i + slice.length, bulkFiles.length);
        setBulkProgress(Math.round((completed / bulkFiles.length) * 100));
        // Mid-flight result render so the user sees what's already in.
        setBulkResults({ ...aggregate });
      }

      setBulkResults(aggregate);
      setBulkProgress(100);

      if (aggregate.successful > 0) {
        const createdCount = aggregate.successful - aggregate.updated;
        if (aggregate.updated > 0) {
          toast.success(`Added ${createdCount} new, updated ${aggregate.updated} existing candidates!`);
        } else {
          toast.success(`Successfully added ${aggregate.successful} candidates!`);
        }
        fetchCandidates();
      }

      if (aggregate.failed > 0) {
        toast.warning(`${aggregate.failed} files failed to process.`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Bulk upload failed'));
    } finally {
      setBulkUploading(false);
    }
  };

  // Excel upload handlers
  const handleExcelFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx, .xls) are allowed');
      return;
    }
    
    setExcelFile(file);
    setExcelResults(null);
  };

  const handleExcelUpload = async () => {
    if (!excelFile) return;

    setExcelUploading(true);
    setExcelProgress(0);
    setExcelResults(null);

    try {
      const response = await candidatesApi.bulkUploadExcel(excelFile, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setExcelProgress(percentCompleted);
      });

      setExcelResults(response.data);
      setExcelProgress(100);
      
      if (response.data.successful > 0 || response.data.updated > 0) {
        const newCount = response.data.successful || 0;
        const updatedCount = response.data.updated || 0;
        
        if (newCount > 0 && updatedCount > 0) {
          toast.success(`Added ${newCount} new candidates, updated ${updatedCount} existing!`);
        } else if (newCount > 0) {
          toast.success(`Successfully added ${newCount} candidates from Excel!`);
        } else if (updatedCount > 0) {
          toast.success(`Successfully updated ${updatedCount} existing candidates from Excel!`);
        }
        fetchCandidates();
      }
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} rows failed to process.`);
      }
    } catch (error) {
      console.error('Excel upload error:', error);
      
      // Check for specific error types
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Upload timed out. The file may be too large. Try splitting into smaller files (under 5000 rows).');
      } else if (error.response?.status === 413) {
        toast.error('File too large. Please reduce file size or split into smaller files.');
      } else if (error.response?.status === 502 || error.response?.status === 504) {
        toast.error('Server timeout. The file is being processed but took too long. Please check candidates list and try again with smaller files.');
      } else if (error.response?.data?.detail) {
        toast.error(`Upload failed: ${error.response.data.detail}`);
      } else {
        toast.error(getErrorMessage(error, 'Excel upload failed. Please try with a smaller file or check the file format.'));
      }
    } finally {
      setExcelUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await candidatesApi.downloadExcelTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidate_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const clearExcelUpload = () => {
    setExcelFile(null);
    setExcelResults(null);
    setExcelProgress(0);
  };

  // Bulk re-upload handlers (for matching existing candidates)
  const MAX_REUPLOAD_FILES = 50;

  const handleBulkReuploadFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || f.name.toLowerCase().endsWith('.docx')
    );
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were skipped. Only PDF and DOCX are allowed.');
    }
    
    const newTotal = bulkReuploadFiles.length + validFiles.length;
    if (newTotal > MAX_REUPLOAD_FILES) {
      toast.error(`Maximum ${MAX_REUPLOAD_FILES} files allowed. You can add ${MAX_REUPLOAD_FILES - bulkReuploadFiles.length} more files.`);
      const allowedFiles = validFiles.slice(0, MAX_REUPLOAD_FILES - bulkReuploadFiles.length);
      setBulkReuploadFiles(prev => [...prev, ...allowedFiles]);
    } else {
      setBulkReuploadFiles(prev => [...prev, ...validFiles]);
    }
    setBulkReuploadResults(null);
  };

  const removeBulkReuploadFile = (index) => {
    setBulkReuploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearBulkReupload = () => {
    setBulkReuploadFiles([]);
    setBulkReuploadResults(null);
    setBulkReuploadProgress(0);
    setBulkReuploadDragging(false);
  };

  const handleBulkReupload = async () => {
    if (bulkReuploadFiles.length === 0) return;

    setBulkReuploadUploading(true);
    setBulkReuploadProgress(0);
    setBulkReuploadResults(null);

    // Same chunking story as bulk-upload — see comment above on the
    // Cloudflare 120s budget. Re-upload also does PDF parsing per file
    // so 19+ files in one shot reliably times out in prod.
    const aggregate = {
      total: bulkReuploadFiles.length,
      matched: 0,
      unmatched: 0,
      failed: 0,
      candidates: [],
      errors: [],
    };

    try {
      for (let i = 0; i < bulkReuploadFiles.length; i += BULK_UPLOAD_CHUNK) {
        const slice = bulkReuploadFiles.slice(i, i + BULK_UPLOAD_CHUNK);
        try {
          const response = await candidatesApi.bulkReuploadResumes(slice, () => {});
          const d = response.data || {};
          aggregate.matched += d.matched || 0;
          aggregate.unmatched += d.unmatched || 0;
          aggregate.failed += d.failed || 0;
          aggregate.candidates = aggregate.candidates.concat(d.candidates || []);
          aggregate.errors = aggregate.errors.concat(d.errors || []);
        } catch (chunkErr) {
          aggregate.failed += slice.length;
          slice.forEach((f) => {
            aggregate.errors.push({
              filename: f.name || 'unknown',
              error: chunkErr?.response?.data?.detail || chunkErr?.message || 'Chunk upload failed',
            });
          });
        }
        const completed = Math.min(i + slice.length, bulkReuploadFiles.length);
        setBulkReuploadProgress(Math.round((completed / bulkReuploadFiles.length) * 100));
        setBulkReuploadResults({ ...aggregate });
      }

      setBulkReuploadResults(aggregate);
      setBulkReuploadProgress(100);

      if (aggregate.matched > 0) {
        toast.success(`Successfully matched ${aggregate.matched} resumes to existing candidates!`);
        fetchCandidates();
      }

      if (aggregate.unmatched > 0) {
        toast.warning(`${aggregate.unmatched} resumes could not be matched to existing candidates.`);
      }

      if (aggregate.failed > 0) {
        toast.error(`${aggregate.failed} files failed to process.`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Bulk re-upload failed'));
    } finally {
      setBulkReuploadUploading(false);
    }
  };

  const openEdit = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData({
      ...candidate,
      skills: Array.isArray(candidate.skills) ? candidate.skills.join(', ') : (candidate.skills || ''),
    });
    setIsEditOpen(true);
  };

  const openView = (candidate) => {
    setSelectedCandidate(candidate);
    setIsViewOpen(true);
  };

  // Auto-open the candidate detail dialog when the URL carries `?view=<id>`
  // (used by global header search results so a candidate click opens the
  // individual record instead of just filtering the list).
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    if (!viewId) return;
    // Strip the param immediately so reload-after-close doesn't re-open.
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });

    let cancelled = false;
    (async () => {
      try {
        const res = await candidatesApi.getById(viewId);
        if (!cancelled && res?.data) openView(res.data);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, 'Could not load candidate'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewResume = async (candidate) => {
    // First check if candidate has any resume linked
    if (!candidate?.resume_url && !candidate?.gridfs_resume_id) {
      toast.error(`No resume uploaded for ${candidate?.name || 'this candidate'}. Use "Upload Resume" to add one.`);
      return;
    }
    
    try {
      const response = await candidatesApi.getResume(candidate.id);
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      if (error.response?.status === 404) {
        // File reference exists but actual file is missing - offer to re-upload
        toast.error(
          <div>
            <p className="font-medium">Resume file not found for {candidate?.name || 'this candidate'}</p>
            <p className="text-sm mt-1">The file reference exists but the actual file is missing. Please re-upload the resume.</p>
          </div>,
          { duration: 5000 }
        );
        // Auto-open the upload dialog for convenience
        openResumeUploadDialog(candidate);
      } else {
        toast.error('Failed to load resume');
      }
    }
  };

  // Resume Upload handlers
  const openResumeUploadDialog = (candidate) => {
    setResumeUploadCandidate(candidate);
    setResumeUploadFile(null);
    setIsResumeUploadOpen(true);
  };

  const handleResumeUpload = async () => {
    if (!resumeUploadFile || !resumeUploadCandidate) {
      toast.error('Please select a file');
      return;
    }

    setResumeUploadLoading(true);
    try {
      await candidatesApi.uploadCandidateResume(resumeUploadCandidate.id, resumeUploadFile);
      toast.success(`Resume uploaded for ${resumeUploadCandidate.name || 'candidate'}`);
      setIsResumeUploadOpen(false);
      setResumeUploadCandidate(null);
      setResumeUploadFile(null);
      fetchCandidates(); // Refresh list to show updated status
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload resume'));
    } finally {
      setResumeUploadLoading(false);
    }
  };

  // Add to Workbench handlers
  const openWorkbenchDialog = async (candidate) => {
    setWorkbenchCandidate(candidate);
    setWorkbenchRemark('');
    setWorkbenchStatus('');
    setWorkbenchPrivate(true);
    setWorkbenchPipelineJobId('');
    setIsWorkbenchOpen(true);
    // Lazy-load active jobs the first time the dialog opens (cache afterward).
    if (workbenchJobs.length === 0) {
      try {
        const res = await jobsApi.getAll({ limit: 200 });
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
        // Show all jobs that aren't closed — published/assigned/on_hold (live)
        // and draft (still being prepared but might want to sourced into).
        const openable = list.filter((j) => ['draft', 'published', 'assigned', 'on_hold'].includes(j.status));
        setWorkbenchJobs(openable);
      } catch (err) {
        // Non-blocking — user can still save the personal note even if jobs fail to load
        // eslint-disable-next-line no-console
        console.warn('Failed to load jobs for workbench picker', err);
      }
    }
  };

  // iter_39: Bulk shortlist handlers
  const toggleCandidateSelection = (id) => {
    if (!id) return;
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (checked, visibleIds) => {
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev);
      (visibleIds || []).forEach((id) => {
        if (!id) return;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const clearCandidateSelection = () => setSelectedCandidateIds(new Set());

  const openBulkShortlistDialog = async () => {
    if (selectedCandidateIds.size === 0) {
      toast.error('Select at least one candidate first');
      return;
    }
    setBulkShortlistJobId('');
    setBulkShortlistRemarks('');
    setIsBulkShortlistOpen(true);
    // Reuse the workbench job-cache so we don't re-fetch when both dialogs share jobs.
    if (workbenchJobs.length === 0) {
      try {
        const res = await jobsApi.getAll({ limit: 200 });
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
        const openable = list.filter((j) => ['draft', 'published', 'assigned', 'on_hold'].includes(j.status));
        setWorkbenchJobs(openable);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load jobs for bulk shortlist picker', err);
      }
    }
  };

  const handleBulkShortlist = async () => {
    if (!bulkShortlistJobId) {
      toast.error('Pick a job to shortlist into');
      return;
    }
    const ids = Array.from(selectedCandidateIds);
    if (ids.length === 0) {
      toast.error('No candidates selected');
      return;
    }
    if (ids.length > 50) {
      toast.error('Maximum 50 candidates per bulk shortlist');
      return;
    }
    setBulkShortlistLoading(true);
    try {
      const res = await applicationsApi.bulkShortlist({
        job_id: bulkShortlistJobId,
        candidate_ids: ids,
        stage: 'sourced',
        source: 'candidates_bulk_shortlist',
        remarks: bulkShortlistRemarks?.trim()
          ? `Bulk shortlist from Candidates: ${bulkShortlistRemarks.trim()}`
          : 'Bulk shortlist from Candidates',
      });
      const r = res?.data?.results || res?.data || {};
      const successful = Number(r.successful || 0);
      const skipped = Number(r.skipped || 0);
      const failed = Number(r.failed || 0);
      const job = workbenchJobs.find((j) => j.id === bulkShortlistJobId);
      const jobLabel = job?.title || 'pipeline';
      const parts = [];
      parts.push(`${successful} of ${ids.length} candidate${ids.length === 1 ? '' : 's'} shortlisted to ${jobLabel}`);
      if (skipped > 0) parts.push(`${skipped} already in pipeline`);
      if (failed > 0) parts.push(`${failed} failed`);
      const msg = parts.join(' · ');
      if (successful > 0) {
        toast.success(msg + ' — visible in Command Center');
      } else if (skipped > 0 && failed === 0) {
        toast.info(msg);
      } else {
        toast.error(msg || 'No candidates were shortlisted');
      }
      setIsBulkShortlistOpen(false);
      clearCandidateSelection();
      setBulkShortlistJobId('');
      setBulkShortlistRemarks('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Bulk shortlist failed'));
    } finally {
      setBulkShortlistLoading(false);
    }
  };

  const handleAddToWorkbench = async () => {
    if (!workbenchStatus) {
      toast.error('Please select a status');
      return;
    }

    // Combine status and remark
    const fullRemark = workbenchRemark.trim()
      ? `[${workbenchStatus}] ${workbenchRemark}`
      : `[${workbenchStatus}]`;

    setWorkbenchLoading(true);
    try {
      const remarkRes = await workbenchApi.addRemark(workbenchCandidate.id, fullRemark, workbenchPrivate, workbenchStatus);
      const autoArchived = !!remarkRes?.data?.auto_archived;

      // iter_38: If a job was selected, ALSO shortlist into that job's pipeline so
      // the candidate appears in Command Center.
      let pipelineNotice = '';
      if (workbenchPipelineJobId) {
        const job = workbenchJobs.find((j) => j.id === workbenchPipelineJobId);
        try {
          await applicationsApi.create({
            candidate_id: workbenchCandidate.id,
            job_id: workbenchPipelineJobId,
            stage: 'sourced',
            remarks: `Shortlisted from Candidates → Workbench (${workbenchStatus})`,
          });
          pipelineNotice = ` and shortlisted to ${job?.title || 'pipeline'} — visible in Command Center`;
        } catch (err) {
          const msg = getErrorMessage(err, '');
          if (typeof msg === 'string' && msg.toLowerCase().includes('already applied')) {
            pipelineNotice = ` (already in pipeline for ${job?.title || 'this job'})`;
          } else {
            // Soft-fail — workbench note is already saved, surface a non-fatal toast
            toast.error(`Saved note, but couldn't add to pipeline: ${msg || 'unknown error'}`);
          }
        }
      }

      toast.success(
        autoArchived
          ? `${workbenchCandidate.name || 'Candidate'} auto-archived from workbench (${workbenchStatus})${pipelineNotice}`
          : `${workbenchCandidate.name || 'Candidate'} added to your workbench${pipelineNotice}`
      );
      setIsWorkbenchOpen(false);
      setWorkbenchCandidate(null);
      setWorkbenchRemark('');
      setWorkbenchStatus('');
      setWorkbenchPipelineJobId('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add to workbench'));
    } finally {
      setWorkbenchLoading(false);
    }
  };

  // Search filtering is now done server-side (iter_41 + iter_45).
  // Advanced-search mode bypasses the paginated /candidates endpoint
  // entirely — those results come back fully from /candidates/search.
  const filteredCandidates = candidates;

  const canDelete = ['admin', 'team_lead'].includes(user?.role);

  // Trigger duplicate check when name, email, or phone changes (for add candidate)
  useEffect(() => {
    if (isCreateOpen && (formData.name || formData.email || formData.phone)) {
      debouncedDuplicateCheck(formData.name, formData.email, formData.phone);
    }
  }, [formData.name, formData.email, formData.phone, isCreateOpen, debouncedDuplicateCheck]);

  // Use the extracted CandidateFormContent component to prevent scroll issues
  const renderCandidateForm = (onSubmit, submitText, isEdit = false) => {
    return (
      <CandidateFormContent
        formData={formData}
        setFormData={setFormData}
        onSubmit={onSubmit}
        submitText={submitText}
        isEdit={isEdit}
        formLoading={formLoading}
        showDuplicatesWarning={showDuplicatesWarning}
        duplicates={duplicates}
        checkingDuplicates={checkingDuplicates}
        setShowDuplicatesWarning={setShowDuplicatesWarning}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="candidates-page">
      {/* Header — `flex-wrap` lets the toolbar drop to a second row on
          smaller screens (≤1440px laptops) so the 7+ action buttons never
          force the H1 into overflow / under the fixed sidebar. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Candidates
            </h1>
            {isAdminUser && brokenResumeCount > 0 && (
              <Link
                to="/settings"
                data-testid="broken-resume-badge"
                title="Click to open Data Maintenance and clean up broken resume links"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {brokenResumeCount} broken resume link{brokenResumeCount === 1 ? '' : 's'}
              </Link>
            )}
          </div>
          <p className="text-slate-600 mt-1">Resume bank & candidate database</p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end" data-testid="candidates-toolbar">
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-resume-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload Resume
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Resume</DialogTitle>
                <DialogDescription>Upload a PDF or DOCX resume for AI parsing</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="resume-upload"
                  />
                  <label htmlFor="resume-upload" className="cursor-pointer">
                    {uploadLoading ? (
                      <Loader2 className="w-8 h-8 mx-auto text-teal-700 animate-spin" />
                    ) : (
                      <FileText className="w-8 h-8 mx-auto text-slate-400" />
                    )}
                    <p className="mt-2 text-sm text-slate-600">
                      {uploadLoading ? 'Uploading...' : 'Click to upload PDF or DOCX'}
                    </p>
                  </label>
                </div>

                {uploadedResumeText && (
                  <div className="space-y-2">
                    <Label>Extracted Text Preview</Label>
                    <div className="max-h-40 overflow-y-auto bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                      {uploadedResumeText.slice(0, 500)}...
                    </div>
                    <Button
                      onClick={handleParseResume}
                      disabled={parseLoading}
                      className="w-full bg-teal-700 hover:bg-teal-800"
                      data-testid="parse-resume-btn"
                    >
                      {parseLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Parse with AI
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk Upload Dialog */}
          <Dialog open={isBulkUploadOpen} onOpenChange={(open) => {
            setIsBulkUploadOpen(open);
            if (!open) clearBulkUpload();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-upload-btn">
                <Files className="w-4 h-4 mr-2" /> Bulk Resumes
              </Button>
            </DialogTrigger>

          {/* Excel Upload Dialog */}
          <Dialog open={isExcelUploadOpen} onOpenChange={(open) => {
            setIsExcelUploadOpen(open);
            if (!open) clearExcelUpload();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="excel-upload-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  Import Candidates from Excel
                </DialogTitle>
                <DialogDescription>
                  Upload an Excel file with candidate details. Supports up to 10,000 candidates in one file.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Download Template */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-900">Download Template</p>
                      <p className="text-sm text-blue-700">Get a sample Excel file with correct column headers</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                      <Download className="w-4 h-4 mr-2" /> Template
                    </Button>
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Select Excel File</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelFileSelect}
                      className="hidden"
                      id="excel-file-upload"
                      disabled={excelUploading}
                    />
                    <label htmlFor="excel-file-upload" className="flex-1">
                      <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                        excelFile ? 'border-green-400 bg-green-50' : 'border-slate-200 hover:border-teal-400'
                      }`}>
                        {excelFile ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileSpreadsheet className="w-8 h-8 text-green-600" />
                            <div className="text-left">
                              <p className="font-medium text-slate-900">{excelFile.name}</p>
                              <p className="text-sm text-slate-500">{(excelFile.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                            <p className="text-slate-600">Click to select Excel file</p>
                            <p className="text-xs text-slate-400 mt-1">.xlsx or .xls • Max 10,000 rows</p>
                          </>
                        )}
                      </div>
                    </label>
                    {excelFile && !excelUploading && (
                      <Button variant="ghost" size="icon" onClick={clearExcelUpload}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Required Columns Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-medium text-slate-700 mb-2">Required Columns:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="default">name *</Badge>
                    <Badge variant="default">email *</Badge>
                    <Badge variant="default">phone *</Badge>
                    <Badge variant="outline">skills</Badge>
                    <Badge variant="outline">experience_years</Badge>
                    <Badge variant="outline">current_company</Badge>
                    <Badge variant="outline">current_designation</Badge>
                    <Badge variant="outline">current_location</Badge>
                    <Badge variant="outline">current_ctc</Badge>
                    <Badge variant="outline">expected_ctc</Badge>
                    <Badge variant="outline">notice_period</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">* Required fields. Skills should be comma-separated.</p>
                </div>

                {/* Progress Bar */}
                {excelUploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Processing Excel file...</span>
                      <span className="font-medium">{excelProgress}%</span>
                    </div>
                    <Progress value={excelProgress} className="h-2" />
                    <p className="text-xs text-slate-500">
                      This may take several minutes for large files. Please don't close this page.
                    </p>
                  </div>
                )}

                {/* Results */}
                {excelResults && (
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Upload Results</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">{excelResults.total}</p>
                        <p className="text-xs text-slate-500">Total Rows</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-700">{excelResults.successful}</p>
                        <p className="text-xs text-green-600">Added</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{excelResults.duplicates}</p>
                        <p className="text-xs text-amber-500">Duplicates</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{excelResults.failed}</p>
                        <p className="text-xs text-red-500">Failed</p>
                      </div>
                    </div>
                    {excelResults.errors?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-red-600 mb-2">Errors (first 100):</p>
                        <ScrollArea className="h-32 border rounded-lg p-2 bg-white">
                          {excelResults.errors.map((err, idx) => (
                            <p key={idx} className="text-xs text-red-600">
                              Row {err.row}: {err.error}
                            </p>
                          ))}
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsExcelUploadOpen(false)}>
                  {excelResults ? 'Close' : 'Cancel'}
                </Button>
                {!excelResults && (
                  <Button
                    onClick={handleExcelUpload}
                    disabled={!excelFile || excelUploading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {excelUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Candidates
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bulk Re-upload Dialog (Match to existing candidates) */}
          <Dialog open={isBulkReuploadOpen} onOpenChange={(open) => {
            setIsBulkReuploadOpen(open);
            if (!open) clearBulkReupload();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="bulk-reupload-btn">
                <Upload className="w-4 h-4 mr-2" /> Re-Upload Resumes
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-amber-600" />
                  Bulk Re-Upload Resumes
                </DialogTitle>
                <DialogDescription>
                  Upload resumes to match and update EXISTING candidates. Matches by email or phone number extracted from the resume.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>How it works:</strong> Upload resumes and the system will automatically extract email/phone and match them to existing candidates in your database. Perfect for backfilling resumes for candidates imported via Excel.
                  </p>
                </div>
                
                {/* Drag & Drop Zone */}
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                    bulkReuploadDragging 
                      ? 'border-amber-500 bg-amber-50 scale-[1.02]' 
                      : 'border-slate-200 hover:border-amber-400 hover:bg-slate-50'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBulkReuploadDragging(true);
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBulkReuploadDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBulkReuploadDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBulkReuploadDragging(false);
                    
                    const files = Array.from(e.dataTransfer.files).filter(
                      file => file.name.toLowerCase().endsWith('.pdf') || file.name.toLowerCase().endsWith('.docx')
                    );
                    
                    if (files.length === 0) {
                      toast.error('Please drop PDF or DOCX files only');
                      return;
                    }
                    
                    const newTotal = bulkReuploadFiles.length + files.length;
                    if (newTotal > MAX_REUPLOAD_FILES) {
                      toast.error(`Maximum ${MAX_REUPLOAD_FILES} files allowed.`);
                      const allowedFiles = files.slice(0, MAX_REUPLOAD_FILES - bulkReuploadFiles.length);
                      if (allowedFiles.length > 0) {
                        setBulkReuploadFiles(prev => [...prev, ...allowedFiles]);
                      }
                    } else {
                      setBulkReuploadFiles(prev => [...prev, ...files]);
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    multiple
                    onChange={handleBulkReuploadFileSelect}
                    className="hidden"
                    id="bulk-reupload-input"
                    disabled={bulkReuploadUploading}
                  />
                  <label htmlFor="bulk-reupload-input" className="cursor-pointer block">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-colors ${
                      bulkReuploadDragging ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <Upload className={`w-8 h-8 ${bulkReuploadDragging ? 'text-amber-600' : 'text-slate-400'}`} />
                    </div>
                    <p className={`text-base font-medium ${bulkReuploadDragging ? 'text-amber-700' : 'text-slate-700'}`}>
                      {bulkReuploadDragging ? 'Drop files here!' : 'Drag & drop resumes here'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      or <span className="text-amber-600 font-medium">browse files</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      PDF, DOCX • Max 50 files • Matches by email or phone
                    </p>
                  </label>
                </div>

                {/* Selected Files List */}
                {bulkReuploadFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-amber-600" />
                        {bulkReuploadFiles.length} file(s) ready to match
                      </Label>
                      {!bulkReuploadUploading && (
                        <Button variant="ghost" size="sm" onClick={clearBulkReupload}>
                          Clear All
                        </Button>
                      )}
                    </div>
                    <ScrollArea className="h-32 border rounded-lg p-2">
                      <div className="space-y-2">
                        {bulkReuploadFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="text-sm truncate max-w-[300px]">{file.name}</span>
                              <span className="text-xs text-slate-400">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            {!bulkReuploadUploading && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => removeBulkReuploadFile(index)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Progress Bar */}
                {bulkReuploadUploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Matching resumes to candidates...</span>
                      <span className="font-medium">{bulkReuploadProgress}%</span>
                    </div>
                    <Progress value={bulkReuploadProgress} className="h-2" />
                  </div>
                )}

                {/* Results */}
                {bulkReuploadResults && (
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Re-Upload Results</h4>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-700">{bulkReuploadResults.matched}</p>
                        <p className="text-xs text-green-600">Matched & Updated</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{bulkReuploadResults.unmatched}</p>
                        <p className="text-xs text-amber-500">Not Matched</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{bulkReuploadResults.failed}</p>
                        <p className="text-xs text-red-500">Failed</p>
                      </div>
                    </div>

                    {/* Matched Candidates */}
                    {bulkReuploadResults.updated_candidates?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Successfully Matched:
                        </p>
                        <ScrollArea className="max-h-32">
                          {bulkReuploadResults.updated_candidates.map((c, i) => (
                            <p key={i} className="text-sm text-slate-600 pl-5">
                              • {c.name || 'Unnamed'} ({c.match_type}: {c.email || c.phone}) ← {c.filename}
                            </p>
                          ))}
                        </ScrollArea>
                      </div>
                    )}

                    {/* Unmatched Files */}
                    {bulkReuploadResults.unmatched_files?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-amber-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> Not Matched (no candidate found):
                        </p>
                        <ScrollArea className="max-h-32">
                          {bulkReuploadResults.unmatched_files.map((f, i) => (
                            <div key={i} className="text-sm text-amber-600 pl-5 mb-1">
                              <p>• {f.filename}</p>
                              {f.extracted_emails?.length > 0 && (
                                <p className="text-xs text-slate-500 pl-3">Emails found: {f.extracted_emails.join(', ')}</p>
                              )}
                              {f.extracted_phones?.length > 0 && (
                                <p className="text-xs text-slate-500 pl-3">Phones found: {f.extracted_phones.join(', ')}</p>
                              )}
                            </div>
                          ))}
                        </ScrollArea>
                      </div>
                    )}

                    {/* Errors */}
                    {bulkReuploadResults.errors?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> Errors:
                        </p>
                        <ScrollArea className="max-h-24">
                          {bulkReuploadResults.errors.map((e, i) => (
                            <p key={i} className="text-sm text-red-500 pl-5">
                              • {e.filename}: {e.error}
                            </p>
                          ))}
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                {bulkReuploadResults ? (
                  <Button
                    onClick={() => {
                      setIsBulkReuploadOpen(false);
                      clearBulkReupload();
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Done
                  </Button>
                ) : (
                  <Button
                    onClick={handleBulkReupload}
                    disabled={bulkReuploadUploading || bulkReuploadFiles.length === 0}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="start-bulk-reupload-btn"
                  >
                    {bulkReuploadUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Matching...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Match & Upload ({bulkReuploadFiles.length} files)
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Bulk Resume Upload</DialogTitle>
                <DialogDescription>
                  Drag and drop multiple resumes or click to browse. Each will be parsed with AI.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Drag & Drop Zone */}
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                    isDragging 
                      ? 'border-teal-500 bg-teal-50 scale-[1.02]' 
                      : 'border-slate-200 hover:border-teal-400 hover:bg-slate-50'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    
                    const files = Array.from(e.dataTransfer.files).filter(
                      file => file.type === 'application/pdf' || 
                              file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                              file.name.endsWith('.doc') ||
                              file.name.endsWith('.docx') ||
                              file.name.endsWith('.pdf')
                    );
                    
                    if (files.length === 0) {
                      toast.error('Please drop PDF or DOCX files only');
                      return;
                    }
                    
                    const newTotal = bulkFiles.length + files.length;
                    if (newTotal > MAX_BULK_FILES) {
                      toast.error(`Maximum ${MAX_BULK_FILES} files allowed. You can add ${MAX_BULK_FILES - bulkFiles.length} more files.`);
                      const allowedFiles = files.slice(0, MAX_BULK_FILES - bulkFiles.length);
                      if (allowedFiles.length > 0) {
                        setBulkFiles(prev => [...prev, ...allowedFiles]);
                        toast.success(`Added ${allowedFiles.length} file(s)`);
                      }
                    } else {
                      setBulkFiles(prev => [...prev, ...files]);
                      toast.success(`Added ${files.length} file(s)`);
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc"
                    multiple
                    onChange={handleBulkFileSelect}
                    className="hidden"
                    id="bulk-resume-upload"
                    disabled={bulkUploading}
                  />
                  <label htmlFor="bulk-resume-upload" className="cursor-pointer block">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-colors ${
                      isDragging ? 'bg-teal-100' : 'bg-slate-100'
                    }`}>
                      <Upload className={`w-8 h-8 ${isDragging ? 'text-teal-600' : 'text-slate-400'}`} />
                    </div>
                    <p className={`text-base font-medium ${isDragging ? 'text-teal-700' : 'text-slate-700'}`}>
                      {isDragging ? 'Drop files here!' : 'Drag & drop resumes here'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      or <span className="text-teal-600 font-medium">browse files</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Supports PDF, DOCX • Max 20 files at a time
                    </p>
                  </label>
                </div>

                {/* Selected Files List */}
                {bulkFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-teal-600" />
                        {bulkFiles.length} file(s) ready to upload
                      </Label>
                      {!bulkUploading && (
                        <Button variant="ghost" size="sm" onClick={clearBulkUpload}>
                          Clear All
                        </Button>
                      )}
                    </div>
                    <ScrollArea className="h-40 border rounded-lg p-2">
                      <div className="space-y-2">
                        {bulkFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="text-sm truncate max-w-[300px]">{file.name}</span>
                              <span className="text-xs text-slate-400">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            {!bulkUploading && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => removeBulkFile(index)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Progress Bar */}
                {bulkUploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Processing resumes...</span>
                      <span className="font-medium">{bulkProgress}%</span>
                    </div>
                    <Progress value={bulkProgress} className="h-2" />
                    <p className="text-xs text-slate-500">
                      AI is parsing each resume. Please don't close this page.
                    </p>
                  </div>
                )}

                {/* Overwrite Option */}
                {bulkFiles.length > 0 && !bulkUploading && !bulkResults && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="bulk-overwrite"
                      checked={bulkOverwrite}
                      onChange={(e) => setBulkOverwrite(e.target.checked)}
                      className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <label htmlFor="bulk-overwrite" className="text-sm text-amber-800">
                      <span className="font-medium">Overwrite existing candidates</span>
                      <span className="block text-xs text-amber-600">
                        If a candidate with same email exists, update their resume and details
                      </span>
                    </label>
                  </div>
                )}

                {/* Results */}
                {bulkResults && (
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Upload Results</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">{bulkResults.total}</p>
                        <p className="text-xs text-slate-500">Total Files</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-700">{bulkResults.successful - (bulkResults.updated || 0)}</p>
                        <p className="text-xs text-green-600">Created</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-700">{bulkResults.updated || 0}</p>
                        <p className="text-xs text-blue-600">Updated</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{bulkResults.failed}</p>
                        <p className="text-xs text-red-500">Failed</p>
                      </div>
                    </div>

                    {/* Successful Candidates */}
                    {bulkResults.candidates?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Added Candidates:
                        </p>
                        <div className="max-h-32 overflow-y-auto">
                          {bulkResults.candidates.map((c, i) => (
                            <p key={i} className="text-sm text-slate-600 pl-5">
                              • {c.name || 'Unnamed'} {c.email && `(${c.email})`}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Errors */}
                    {bulkResults.errors?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> Errors:
                        </p>
                        <div className="max-h-32 overflow-y-auto">
                          {bulkResults.errors.map((e, i) => (
                            <p key={i} className="text-sm text-red-500 pl-5">
                              • {e.filename}: {e.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                {bulkResults ? (
                  <Button
                    onClick={() => {
                      setIsBulkUploadOpen(false);
                      clearBulkUpload();
                    }}
                    className="bg-teal-700 hover:bg-teal-800"
                  >
                    Done
                  </Button>
                ) : (
                  <Button
                    onClick={handleBulkUpload}
                    disabled={bulkUploading || bulkFiles.length === 0}
                    className="bg-teal-700 hover:bg-teal-800"
                    data-testid="start-bulk-upload-btn"
                  >
                    {bulkUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Upload & Parse ({bulkFiles.length} files)
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800" data-testid="add-candidate-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Candidate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Candidate</DialogTitle>
                <DialogDescription>Enter candidate details</DialogDescription>
              </DialogHeader>
              {renderCandidateForm(handleCreate, "Add Candidate", false)}
            </DialogContent>
          </Dialog>

          {/* iter_42 — Find Duplicates */}
          <Button
            variant="outline"
            onClick={() => setIsFindDuplicatesOpen(true)}
            data-testid="find-duplicates-btn"
            title="Scan for duplicate candidates and merge them"
          >
            <Copy className="w-4 h-4 mr-2 text-amber-600" />
            Find Duplicates
          </Button>

          {/* iter_49 — One-shot DOB backfill (admin only) */}
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              onClick={handleBackfillDob}
              disabled={backfillLoading}
              data-testid="backfill-dob-btn"
              title="Re-scan resume text of candidates missing DOB and auto-extract Date of Birth"
            >
              {backfillLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin text-teal-600" />
              ) : (
                <Cake className="w-4 h-4 mr-2 text-teal-600" />
              )}
              {backfillLoading ? 'Backfilling…' : 'Backfill DOB'}
            </Button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Toggle between simple and advanced search */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch
                id="advanced-search"
                checked={advancedSearchMode}
                onCheckedChange={(checked) => {
                  setAdvancedSearchMode(checked);
                  if (!checked) {
                    clearAdvancedSearch();
                  }
                }}
              />
              <Label htmlFor="advanced-search" className="flex items-center gap-2 cursor-pointer">
                <Zap className={`w-4 h-4 ${advancedSearchMode ? 'text-teal-600' : 'text-slate-400'}`} />
                Advanced Boolean Search
              </Label>
            </div>
            {advancedSearchMode && (
              <div className="flex items-center gap-2">
                {/* Saved Searches Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      data-testid="saved-searches-btn"
                    >
                      <Bookmark className="w-4 h-4" />
                      Saved ({savedSearches.length})
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    {savedSearches.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No saved searches yet
                      </div>
                    ) : (
                      <ScrollArea className="max-h-64">
                        {savedSearches.map((search) => (
                          <div key={search.id} className="flex items-center justify-between p-2 hover:bg-slate-50">
                            <button
                              onClick={() => handleRunSavedSearch(search)}
                              className="flex-1 text-left"
                            >
                              <p className="font-medium text-sm text-slate-900">{search.name || 'Unnamed Search'}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[180px]">{search.query || ''}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {(search.use_count || 0) > 0 && (
                                  <span className="text-xs text-slate-400">Used {search.use_count}x</span>
                                )}
                              </div>
                            </button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-400 hover:text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSavedSearch(search.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </ScrollArea>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSearchHelp(!showSearchHelp)}
                  data-testid="search-help-btn"
                >
                  <HelpCircle className="w-4 h-4 mr-1" />
                  {showSearchHelp ? 'Hide' : 'Show'} Help
                </Button>
              </div>
            )}
          </div>

          {/* Search Help Panel */}
          {advancedSearchMode && showSearchHelp && (
            <div className="p-4 bg-slate-50 rounded-lg text-sm space-y-3">
              <h4 className="font-semibold text-slate-900">Boolean Search Guide</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-slate-700 mb-1">Operators</p>
                  <ul className="space-y-1 text-slate-600">
                    <li><code className="bg-slate-200 px-1 rounded">AND</code> - Both terms required</li>
                    <li><code className="bg-slate-200 px-1 rounded">OR</code> - Either term matches</li>
                    <li><code className="bg-slate-200 px-1 rounded">NOT</code> or <code className="bg-slate-200 px-1 rounded">-</code> - Exclude term</li>
                    <li><code className="bg-slate-200 px-1 rounded">&quot;...&quot;</code> - Exact phrase</li>
                    <li><code className="bg-slate-200 px-1 rounded">( )</code> - Group terms</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-slate-700 mb-1">Field Search</p>
                  <ul className="space-y-1 text-slate-600">
                    <li><code className="bg-slate-200 px-1 rounded">skills:python</code> - Skills filter</li>
                    <li><code className="bg-slate-200 px-1 rounded">location:bangalore</code> - Location filter</li>
                    <li><code className="bg-slate-200 px-1 rounded">company:google</code> - Company filter</li>
                    <li><code className="bg-slate-200 px-1 rounded">exp:3-5</code> - Experience range</li>
                    <li><code className="bg-slate-200 px-1 rounded">title:engineer</code> - Job title</li>
                  </ul>
                </div>
              </div>
              <div>
                <p className="font-medium text-slate-700 mb-1">Examples</p>
                <div className="space-y-1 text-slate-600">
                  <p><code className="bg-teal-100 px-1 rounded text-teal-800">python AND java</code> - Has both Python and Java</p>
                  <p><code className="bg-teal-100 px-1 rounded text-teal-800">skills:react AND location:bangalore</code> - React devs in Bangalore</p>
                  <p><code className="bg-teal-100 px-1 rounded text-teal-800">(python OR java) AND NOT junior</code> - Python/Java, not junior</p>
                  <p><code className="bg-teal-100 px-1 rounded text-teal-800">&quot;senior developer&quot; AND exp:5</code> - Senior dev, 5+ years</p>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Search Form */}
          {advancedSearchMode ? (
            <div className="space-y-4">
              {/* Search Query Row */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-600" />
                    <Input
                      placeholder='e.g., skills:python AND location:bangalore OR "senior developer"'
                      className="pl-9 border-teal-200 focus:border-teal-500"
                      value={advancedQuery}
                      onChange={(e) => setAdvancedQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAdvancedSearch()}
                      data-testid="advanced-search-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Filters Row — wraps to multiple lines on smaller screens */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Min Exp (yrs)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    className="h-9"
                    value={minExp}
                    onChange={(e) => setMinExp(e.target.value)}
                    min={0}
                    data-testid="min-exp-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Max Exp (yrs)</Label>
                  <Input
                    type="number"
                    placeholder="20"
                    className="h-9"
                    value={maxExp}
                    onChange={(e) => setMaxExp(e.target.value)}
                    min={0}
                    data-testid="max-exp-input"
                  />
                </div>
                {showRevenue && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Min Current CTC (L)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    className="h-9"
                    value={minCurrentCtc}
                    onChange={(e) => setMinCurrentCtc(e.target.value)}
                    min={0}
                    step={0.5}
                    data-testid="min-current-ctc-input"
                  />
                </div>
                )}
                {showRevenue && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Max Current CTC (L)</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    className="h-9"
                    value={maxCurrentCtc}
                    onChange={(e) => setMaxCurrentCtc(e.target.value)}
                    min={0}
                    step={0.5}
                    data-testid="max-current-ctc-input"
                  />
                </div>
                )}
                {showRevenue && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Min Expected CTC (L)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    className="h-9"
                    value={minExpectedCtc}
                    onChange={(e) => setMinExpectedCtc(e.target.value)}
                    min={0}
                    step={0.5}
                    data-testid="min-expected-ctc-input"
                  />
                </div>
                )}
                {showRevenue && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Max Expected CTC (L)</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    className="h-9"
                    value={maxExpectedCtc}
                    onChange={(e) => setMaxExpectedCtc(e.target.value)}
                    min={0}
                    step={0.5}
                    data-testid="max-expected-ctc-input"
                  />
                </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Max Notice (days)</Label>
                  <Input
                    type="number"
                    placeholder="90"
                    className="h-9"
                    value={maxNoticePeriod}
                    onChange={(e) => setMaxNoticePeriod(e.target.value)}
                    min={0}
                    data-testid="max-notice-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                    Education
                    <span className="text-slate-400 font-normal" title="Comma-separated, OR-matched (e.g. B.Tech, MCA)">ⓘ</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="B.Tech, MCA"
                    className="h-9"
                    value={educationFilter}
                    onChange={(e) => setEducationFilter(e.target.value)}
                    data-testid="education-filter-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                    Location
                    <span className="text-slate-400 font-normal" title="Pick from existing locations or type a new one. Multi-select, OR-matched.">ⓘ</span>
                  </Label>
                  <LocationCombobox
                    value={locationFilterAdv}
                    onChange={setLocationFilterAdv}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Min Age</Label>
                  <Input
                    type="number"
                    placeholder="22"
                    className="h-9"
                    value={minAge}
                    onChange={(e) => setMinAge(e.target.value)}
                    min={0}
                    max={120}
                    data-testid="min-age-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Max Age</Label>
                  <Input
                    type="number"
                    placeholder="55"
                    className="h-9"
                    value={maxAge}
                    onChange={(e) => setMaxAge(e.target.value)}
                    min={0}
                    max={120}
                    data-testid="max-age-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleAdvancedSearch} 
                    className="bg-teal-700 hover:bg-teal-800 w-full h-9"
                    disabled={!advancedQuery && !minExp && !maxExp && !minCurrentCtc && !maxCurrentCtc && !minExpectedCtc && !maxExpectedCtc && !maxNoticePeriod && !educationFilter && !locationFilterAdv && minAge === '' && maxAge === ''}
                    data-testid="advanced-search-btn"
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Search
                  </Button>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="flex items-center gap-2">
                {(advancedQuery || educationFilter || locationFilterAdv || minAge !== '' || maxAge !== '') && (
                  <Button
                    variant="outline"
                    onClick={() => setIsSaveSearchOpen(true)}
                    data-testid="save-search-btn"
                  >
                    <BookmarkPlus className="w-4 h-4" />
                  </Button>
                )}
                {(advancedQuery || minExp || maxExp || minCurrentCtc || maxCurrentCtc || minExpectedCtc || maxExpectedCtc || maxNoticePeriod || educationFilter || locationFilterAdv || minAge !== '' || maxAge !== '' || searchResults) && (
                  <Button variant="outline" onClick={clearAdvancedSearch}>
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
              
              {/* Search Results Info */}
              {searchResults && (
                <div className="flex items-center gap-2 text-sm text-slate-600 bg-teal-50 p-3 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-teal-600" />
                  <span>
                    Found <strong className="text-teal-700">{searchResults.total}</strong> candidates
                    {advancedQuery && <> matching: <code className="bg-teal-100 px-1 rounded">{advancedQuery}</code></>}
                  </span>
                </div>
              )}

              {/* Save Search Dialog */}
              <Dialog open={isSaveSearchOpen} onOpenChange={setIsSaveSearchOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Save Search</DialogTitle>
                    <DialogDescription>
                      Save this search query for quick access later
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Search Name</Label>
                      <Input
                        value={saveSearchName}
                        onChange={(e) => setSaveSearchName(e.target.value)}
                        placeholder="e.g., Senior Python Devs in Bangalore"
                        data-testid="save-search-name-input"
                      />
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600 mb-1">Query:</p>
                      <code className="text-sm text-teal-700">{advancedQuery}</code>
                      {(minExp || maxExp) && (
                        <p className="text-xs text-slate-500 mt-1">
                          Experience: {minExp || '0'} - {maxExp || 'any'} years
                        </p>
                      )}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsSaveSearchOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveSearch}
                      disabled={savingSearch || !saveSearchName.trim()}
                      className="bg-teal-700 hover:bg-teal-800"
                      data-testid="confirm-save-search-btn"
                    >
                      {savingSearch ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Bookmark className="w-4 h-4 mr-2" />
                      )}
                      Save Search
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            /* Simple Search Form */
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, company..."
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="candidate-search-input"
                  />
                </div>
              </div>
              <Input
                placeholder="Filter by skills (comma-separated)"
                className="w-[200px]"
                value={skillsFilter}
                onChange={(e) => setSkillsFilter(e.target.value)}
              />
              <Input
                placeholder="Filter by location"
                className="w-[150px]"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              />
              {/* Sort + Uploaded-by filter (added Feb 2026) */}
              <select
                value={`${sortBy}:${sortOrder}`}
                onChange={(e) => {
                  const [sb, so] = e.target.value.split(':');
                  setSortBy(sb);
                  setSortOrder(so);
                }}
                className="h-10 w-[200px] rounded-md border border-slate-200 bg-white px-2.5 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                data-testid="candidates-sort-select"
                title="Sort candidates"
              >
                <option value="created_at:desc">Newest first</option>
                <option value="created_at:asc">Oldest first</option>
                <option value="updated_at:desc">Recently edited</option>
                <option value="updated_at:asc">Longest untouched</option>
                <option value="name:asc">Name (A → Z)</option>
                <option value="name:desc">Name (Z → A)</option>
                <option value="experience_years:desc">Experience (high → low)</option>
                <option value="experience_years:asc">Experience (low → high)</option>
              </select>
              <UserPicker
                users={teammates}
                currentUserId={user?.id}
                value={uploadedByFilter}
                onChange={setUploadedByFilter}
                label="Uploaded by"
                testid="candidates-uploaded-by-select"
              />
              <Button
                type="button"
                variant={uploadedByFilter === 'me' ? 'default' : 'outline'}
                onClick={() => setUploadedByFilter(uploadedByFilter === 'me' ? '' : 'me')}
                className={uploadedByFilter === 'me' ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}
                data-testid="candidates-my-uploads-toggle"
                title="Show only candidates you uploaded"
              >
                My uploads
              </Button>
              <Button onClick={handleSearch} variant="secondary" data-testid="search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Candidates Table */}
      <Card>
        <CardContent className="p-0">
          {/* Slim toolbar above the table — currently houses the columns
              gear; future actions (sort menu, density toggle) can land here. */}
          <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-100">
            <DensityToggle density={density} onChange={setDensity} testid="candidates-density" />
            <ColumnsMenu
              columns={CANDIDATES_COLUMNS}
              hiddenKeys={hiddenCols}
              onChange={setHiddenCols}
              testid="candidates-columns-menu"
            />
          </div>
          {/* iter_39 — Bulk action toolbar (visible only when something is selected) */}
          {selectedCandidateIds.size > 0 && (
            <div
              className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-teal-200 bg-teal-50 px-4 py-2.5"
              data-testid="candidates-bulk-action-bar"
            >
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-teal-700" />
                <span className="font-semibold text-teal-900" data-testid="candidates-bulk-selection-count">
                  {selectedCandidateIds.size} candidate{selectedCandidateIds.size === 1 ? '' : 's'} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCandidateSelection}
                  className="h-7 text-slate-500 hover:text-slate-700"
                  data-testid="candidates-bulk-clear-btn"
                >
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-8 bg-teal-700 hover:bg-teal-800"
                  onClick={openBulkShortlistDialog}
                  data-testid="candidates-bulk-add-to-pipeline-btn"
                >
                  <Briefcase className="w-3.5 h-3.5 mr-1.5" />
                  Add to Pipeline
                </Button>
              </div>
            </div>
          )}
          <CandidateTable
            candidates={filteredCandidates}
            onView={openView}
            onEdit={openEdit}
            onDelete={handleDelete}
            onViewResume={handleViewResume}
            onUploadResume={openResumeUploadDialog}
            onAddToWorkbench={openWorkbenchDialog}
            onFindSimilar={handleFindSimilar}
            onAISummary={handleAISummary}
            onJobRecommendations={handleJobRecommendations}
            canDelete={canDelete}
            user={user}
            selectedIds={selectedCandidateIds}
            onToggleSelect={toggleCandidateSelection}
            onToggleSelectAll={toggleSelectAllVisible}
            hiddenCols={hiddenCols}
            densityClass={densityClass}
          />
        </CardContent>
      </Card>

      {/* iter_45 — Pagination footer (only when we're rendering the paginated
          /candidates list, not the /candidates/search advanced result set). */}
      {!advancedSearchMode && pagination.totalPages > 1 && (
        <div
          className="flex items-center justify-between mt-4"
          data-testid="candidates-pagination"
        >
          <p className="text-sm text-slate-600" data-testid="candidates-pagination-summary">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1}
            {' - '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)}
            {' of '}
            {pagination.total} candidates
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              disabled={!pagination.hasPrev}
              data-testid="candidates-pagination-prev-btn"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600" data-testid="candidates-pagination-page-label">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              disabled={!pagination.hasNext}
              data-testid="candidates-pagination-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Candidate</DialogTitle>
            <DialogDescription>Update candidate details</DialogDescription>
          </DialogHeader>
          {renderCandidateForm(handleUpdate, "Update Candidate", true)}
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <CandidateViewDialog
        isOpen={isViewOpen}
        onClose={setIsViewOpen}
        candidate={selectedCandidate}
        onViewResume={handleViewResume}
        user={user}
        onCandidateUpdated={(updated) => {
          if (!updated) return;
          setSelectedCandidate(updated);
          setCandidates((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
        }}
      />

      {/* Resume Upload Dialog */}
      <Dialog open={isResumeUploadOpen} onOpenChange={(open) => {
        setIsResumeUploadOpen(open);
        if (!open) {
          setResumeUploadFile(null);
          setResumeUploadCandidate(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              {resumeUploadCandidate?.resume_url || resumeUploadCandidate?.gridfs_resume_id 
                ? 'Replace Resume' 
                : 'Upload Resume'}
            </DialogTitle>
            <DialogDescription>
              {resumeUploadCandidate?.resume_url || resumeUploadCandidate?.gridfs_resume_id 
                ? `Replace the existing resume for ${resumeUploadCandidate?.name || 'this candidate'}`
                : `Upload a resume for ${resumeUploadCandidate?.name || 'this candidate'}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => setResumeUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                id="resume-upload-input"
                disabled={resumeUploadLoading}
              />
              <label htmlFor="resume-upload-input" className="cursor-pointer">
                {resumeUploadFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-teal-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{resumeUploadFile.name}</p>
                      <p className="text-sm text-slate-500">
                        {(resumeUploadFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <FileText className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Click to select PDF or DOCX</p>
                    <p className="text-xs text-slate-400 mt-1">Max file size: 10MB</p>
                  </>
                )}
              </label>
            </div>
            {resumeUploadFile && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setResumeUploadFile(null)}
              >
                <X className="w-4 h-4 mr-2" /> Clear Selection
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResumeUploadOpen(false)}
              disabled={resumeUploadLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResumeUpload}
              disabled={!resumeUploadFile || resumeUploadLoading}
              className="bg-teal-700 hover:bg-teal-800"
            >
              {resumeUploadLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Resume
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Similar Candidates Modal */}
      <Dialog open={showSimilarModal} onOpenChange={setShowSimilarModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Similar Candidates
            </DialogTitle>
            <DialogDescription>
              Candidates similar to {selectedCandidate?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {duplicates.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No similar candidates found</p>
            ) : (
              <ScrollArea className="max-h-80">
                <div className="space-y-2">
                  {duplicates.map((dup) => (
                    <div 
                      key={dup.id} 
                      className="p-3 bg-slate-50 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{dup.name || 'Unnamed'}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            {dup.email && <span>{dup.email}</span>}
                            {dup.current_company && <span>• {dup.current_company}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant="outline" 
                          className={
                            dup.match_score >= 90 ? 'border-red-200 text-red-700 bg-red-50' :
                            dup.match_score >= 70 ? 'border-amber-200 text-amber-700 bg-amber-50' :
                            'border-slate-200 text-slate-700'
                          }
                        >
                          {Math.round(dup.match_score)}% match
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1 capitalize">
                          {dup.match_type.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSimilarModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Workbench Dialog */}
      <WorkbenchDialog
        isOpen={isWorkbenchOpen}
        onClose={setIsWorkbenchOpen}
        candidate={workbenchCandidate}
        remark={workbenchRemark}
        setRemark={setWorkbenchRemark}
        status={workbenchStatus}
        setStatus={setWorkbenchStatus}
        isPrivate={workbenchPrivate}
        setIsPrivate={setWorkbenchPrivate}
        loading={workbenchLoading}
        onSubmit={handleAddToWorkbench}
        statuses={candidateStatuses}
        jobs={workbenchJobs}
        pipelineJobId={workbenchPipelineJobId}
        setPipelineJobId={setWorkbenchPipelineJobId}
      />

      {/* iter_39 — Bulk Shortlist Dialog */}
      <BulkShortlistDialog
        isOpen={isBulkShortlistOpen}
        onClose={setIsBulkShortlistOpen}
        selectedCount={selectedCandidateIds.size}
        jobs={workbenchJobs}
        jobId={bulkShortlistJobId}
        setJobId={setBulkShortlistJobId}
        remarks={bulkShortlistRemarks}
        setRemarks={setBulkShortlistRemarks}
        loading={bulkShortlistLoading}
        onSubmit={handleBulkShortlist}
      />

      {/* iter_42 — Find Duplicates Dialog */}
      <FindDuplicatesDialog
        isOpen={isFindDuplicatesOpen}
        onClose={setIsFindDuplicatesOpen}
        onMerged={() => fetchCandidates()}
      />

      {/* AI Summary Dialog */}
      <Dialog open={isAISummaryOpen} onOpenChange={setIsAISummaryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              AI Candidate Summary
            </DialogTitle>
            <DialogDescription>
              {selectedCandidate?.name || 'Candidate'} - AI-powered profile analysis
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 max-h-[60vh] pr-4">
            <div className="space-y-4">
              {aiSummaryLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-3" />
                  <p className="text-slate-600">Analyzing candidate profile...</p>
                </div>
              ) : aiSummaryData ? (
                <>
                  {/* Summary */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-purple-800">{aiSummaryData.summary}</p>
                  </div>

                  {/* Hiring Recommendation */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Hiring Recommendation</p>
                      <p className="text-xl font-bold">{aiSummaryData.hiring_recommendation || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Ideal Company Type</p>
                      <p className="text-xl font-bold capitalize">{aiSummaryData.ideal_company_type || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Key Strengths */}
                  {aiSummaryData.key_strengths?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-700">Key Strengths</h4>
                      <div className="flex flex-wrap gap-2">
                        {aiSummaryData.key_strengths.map((strength, i) => (
                          <Badge key={i} className="bg-green-100 text-green-700 border-green-200">
                            {strength}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Potential Concerns */}
                  {aiSummaryData.potential_concerns?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-700">Potential Concerns</h4>
                      <div className="flex flex-wrap gap-2">
                        {aiSummaryData.potential_concerns.map((concern, i) => (
                          <Badge key={i} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            {concern}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommended Roles */}
                  {aiSummaryData.recommended_roles?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-700">Recommended Roles</h4>
                      <div className="flex flex-wrap gap-2">
                        {aiSummaryData.recommended_roles.map((role, i) => (
                          <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Career Trajectory */}
                  {aiSummaryData.career_trajectory && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <h4 className="font-medium text-slate-700 mb-1">Career Trajectory</h4>
                      <p className="text-sm text-slate-600">{aiSummaryData.career_trajectory}</p>
                    </div>
                  )}

                  {/* Education Assessment */}
                  {(aiSummaryData.education || aiSummaryData.education_assessment) && (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                      <h4 className="font-medium text-indigo-700 mb-1">Education</h4>
                      {aiSummaryData.education && (
                        <p className="text-sm text-indigo-900 font-medium mb-1">{aiSummaryData.education}</p>
                      )}
                      {aiSummaryData.education_assessment && (
                        <p className="text-sm text-indigo-600">{aiSummaryData.education_assessment}</p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Brain className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                  <p>No summary available</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAISummaryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Recommendations Dialog */}
      <Dialog open={isRecommendationsOpen} onOpenChange={setIsRecommendationsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Job Recommendations
            </DialogTitle>
            <DialogDescription>
              Best job matches for {selectedCandidate?.name || 'candidate'}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 max-h-[60vh] pr-4">
            <div className="space-y-4">
              {recommendationsLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                  <p className="text-slate-600">Finding best job matches...</p>
                </div>
              ) : recommendationsData?.recommendations?.length > 0 ? (
                <div className="space-y-3">
                  {recommendationsData.recommendations.map((rec, i) => (
                    <div key={i} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{rec.job_title}</h4>
                            <Badge className={
                              rec.match_score >= 80 ? 'bg-green-100 text-green-700' :
                              rec.match_score >= 60 ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }>
                              {rec.match_score}% Match
                            </Badge>
                          </div>
                          {rec.client_name && (
                            <p className="text-sm text-slate-500">{rec.client_name}</p>
                          )}
                          {rec.job_location && (
                            <p className="text-xs text-slate-400 mt-1">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {rec.job_location}
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">{rec.reason}</p>
                      {rec.growth_potential && (
                        <p className="text-xs text-green-600 mt-1">
                          <Sparkles className="w-3 h-3 inline mr-1" />
                          {rec.growth_potential}
                        </p>
                      )}
                      {rec.job_skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {rec.job_skills.slice(0, 4).map((skill, j) => (
                            <Badge key={j} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                  <p>No job recommendations available</p>
                  <p className="text-sm">Make sure there are active jobs in the system</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRecommendationsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Location autocomplete combobox (multi-select chips) ----------
// Source of truth is a comma-separated string in the parent's `locationFilterAdv` state.
// On every add/remove we re-emit a fresh comma-separated string so the rest of the
// search/save/run plumbing keeps working unchanged.
const LocationCombobox = ({ value = '', onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchTimerRef = React.useRef(null);

  // Parse the comma-separated string into a set of selected chips
  const selected = React.useMemo(() => {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [value]);

  // Fetch suggestions when the popover opens or the search query changes
  useEffect(() => {
    if (!open) return;
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await candidatesApi.distinctLocations(search.trim(), 50);
        setItems(res.data?.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200); // 200ms debounce keeps the API quiet while user types
    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    };
  }, [open, search]);

  const emit = (chips) => onChange(chips.join(', '));

  const toggle = (loc) => {
    if (selected.includes(loc)) {
      emit(selected.filter((s) => s !== loc));
    } else {
      emit([...selected, loc]);
    }
  };

  const removeChip = (loc, e) => {
    e?.stopPropagation();
    emit(selected.filter((s) => s !== loc));
  };

  const addCustom = () => {
    const v = search.trim();
    if (!v) return;
    if (!selected.includes(v)) emit([...selected, v]);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="location-filter-input"
          className="w-full min-h-[36px] flex flex-wrap items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-200 text-left"
        >
          {selected.length === 0 ? (
            <span className="text-sm text-slate-400 px-1">Bangalore, Pune…</span>
          ) : (
            <>
              {selected.map((loc) => (
                <span
                  key={loc}
                  className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-teal-50 text-teal-800 border border-teal-200 text-[11px] font-medium"
                  data-testid={`location-chip-${loc.replace(/\s+/g, '_').toLowerCase()}`}
                >
                  {loc}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => removeChip(loc, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') removeChip(loc, e);
                    }}
                    className="hover:text-rose-700 cursor-pointer flex items-center"
                    aria-label={`Remove ${loc}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </span>
                </span>
              ))}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0"
        align="start"
        data-testid="location-filter-popover"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search locations…"
            value={search}
            onValueChange={setSearch}
            data-testid="location-filter-search"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && search.trim() && items.length === 0) {
                e.preventDefault();
                addCustom();
              }
            }}
          />
          <CommandList className="max-h-[280px]">
            {loading ? (
              <div className="flex items-center justify-center py-4 text-slate-400 text-sm">
                Loading…
              </div>
            ) : (
              <>
                {items.length === 0 ? (
                  <CommandEmpty>
                    {search.trim() ? (
                      <div className="text-center py-3 text-sm">
                        <p className="text-slate-500 mb-2">No matches</p>
                        <button
                          type="button"
                          onClick={addCustom}
                          className="text-teal-700 font-medium hover:underline"
                          data-testid="location-add-custom"
                        >
                          Use &quot;{search.trim()}&quot;
                        </button>
                      </div>
                    ) : (
                      'No locations available yet.'
                    )}
                  </CommandEmpty>
                ) : (
                  <CommandGroup heading={`Top locations${search ? ` matching "${search}"` : ''}`}>
                    {items.map((it) => {
                      const isSel = selected.includes(it.location);
                      return (
                        <CommandItem
                          key={it.location}
                          value={it.location}
                          onSelect={() => toggle(it.location)}
                          data-testid={`location-option-${it.location.replace(/\s+/g, '_').toLowerCase()}`}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Check className={`w-3.5 h-3.5 ${isSel ? 'text-teal-600' : 'opacity-0'}`} />
                          <span className="flex-1 text-sm truncate" title={it.location}>
                            {it.location}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">
                            {it.count}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Candidates;
